import moment from 'moment-timezone';
import { isToday, isElasticLoad } from '../helpers/common';
import { secondsToTime } from '../helpers/moment-ex';
import {
    TOPIC_KPI_EG, TOPIC_LATEST_KPI_EG, TOPIC_LATEST_EG_SENSOR, TOPIC_SENSOR, TOPIC_LOAD_EG,
    HARVESTING_CATEGORYIDS, TOPIC_QLOST
} from '../constants';
import { toMilesPerHour } from '../helpers/unit';
// import { DEFAULT_QLOST_SPAN_SEC } from "../components/map/marker/MapMarker";

const egCategoryGroupList = { 1: HARVESTING_CATEGORYIDS, 2: [], 3: [] };

export const filterEgs = (egList, filters) => {
    const { crewIds, egIds, operatorIds, egCategoryGroups } = filters;

    let filteredEgList = egList;
    if (Array.isArray(crewIds))
        filteredEgList = filteredEgList.filter(e => crewIds.indexOf(e.crewId) >= 0);

    if (Array.isArray(egIds))
        filteredEgList = filteredEgList.filter(e => egIds.indexOf(e.egId) >= 0);

    if (Array.isArray(operatorIds))
        filteredEgList = filteredEgList.filter(e => operatorIds.indexOf(e.operatorId) >= 0);

    if (Array.isArray(egCategoryGroups)) {
        let categoryIds = [];
        for (const groupId of egCategoryGroups) {
            if (egCategoryGroupList.hasOwnProperty(groupId))
                categoryIds = [...categoryIds, ...egCategoryGroupList[groupId]];
        }
        if (categoryIds.length > 0) filteredEgList = filteredEgList.filter(e => categoryIds.indexOf(e.categoryId) >= 0);
    }

    return filteredEgList;
}

export const parseMapEGList = (egList, colors) => egList.map((e, i) => ({
    egId: e.equipment_group_uuid,
    crewId: e.crews && e.crews.length > 0 ? e.crews[e.crews.length - 1].crew_uuid : null,
    crews: e.crews,
    isTruck: e.vehicle.is_truck,
    name: e.equipment_group_name,
    operatorId: e.operator && e.operator.operator_uuid ? e.operator.operator_uuid : null,
    operatorName: e.operator && e.operator.name ? e.operator.name : null,
    deviceId: e.phone_device.device_uuid,
    iconUrl: e.img.ic,
    smallImgUrl: e.img.sm,
    gps: e._gps ? e._gps : null,
    qConnection: e._qConnection ? e._qConnection : null,
    color: colors[i % colors.length],
    equipmentTypeId: e.vehicle.equipment_type_id,
    categoryId: e.primary_category_id,
    implement: {
        categoryId: e.implement ? e.implement.equipment_category_id : null,
        subcategoryId: e.implement ? e.implement.equipment_subcategory_id : null,
    },
    vehicleSpec: e.vehicle.specification ? e.vehicle.specification : null
}));

export const parseMapEGKPI = (payload, params) => {
    if (!payload) return;

    const { kpiType, automaticLoad } = params;
    if (kpiType === "baler") return payload;

    if (kpiType !== "truck") {
        return {
            plannedAcres: payload.acres.planned,
            completedAcres: payload.acres.completed
        };
    }

    if (kpiType === "truck" && !automaticLoad) {
        return {
            loadWeight: payload.vehicle.load_capacity,
            speed: payload.vehicle.avg_speed,
            loads: payload.vehicle.loads,
            avgRoundTripLoadTime: payload.vehicle.avg_round_trip_load_time
        };
    }
    if (kpiType === "truck" && automaticLoad) {
        const loadWeight = payload.hasOwnProperty("load_weight") ? parseFloat(payload.load_weight) : 0;
        const loadCount = payload.hasOwnProperty("load_count") ? parseInt(payload.load_count) : 0;
        const roundTripSeconds = payload.hasOwnProperty("round_trip_seconds") ? parseFloat(payload.round_trip_seconds) : 0;
        const avgRoundTripTime = secondsToTime(roundTripSeconds);
        return {
            loadWeight,
            ...loadCount > 0 && { loads: loadCount },
            ...roundTripSeconds > 0 && { avgRoundTripLoadTime: avgRoundTripTime }
        };
    }

    return;
}

export const handleMapEGLiveData = (state, action) => {
    const { message, range } = action;
    const live = message;

    if ([TOPIC_KPI_EG, TOPIC_LATEST_KPI_EG].indexOf(live.name) >= 0 && state.egKPIBoxList.hasOwnProperty(live.deviceId) &&
        state.egKPIBoxList[live.deviceId].hasOwnProperty('completedAcres')) {
        const data = live.initial ? live.message.data : live.message;
        return mergeMapEGKPI(state, data, { egId: live.deviceId, range }, true);
    } else if ([TOPIC_LATEST_EG_SENSOR, TOPIC_SENSOR].indexOf(live.name) >= 0 && isToday(range)) {
        return mergeQPresentFromLive(state, live);
        // return mergeQConnectionFromLive(state, live);
    } else if ([TOPIC_QLOST].indexOf(live.name) >= 0 && isToday(range)) {
        // return mergeQLostFromLive(state, live);
    } else if (live.name === TOPIC_LOAD_EG && isElasticLoad()) {
        const data = live.initial ? live.message.data : live.message;
        return mergeMapEGLoad(state, data);
    }
    return;
}

export const mergeMapEGKPI = (state, message, params, isLive = false) => {
    if (!message) return;

    let completedAcres = 0, utilization = 0;
    if (isToday(params.range) && isLive) {
        completedAcres = message.summary && message.summary.work && !isNaN(message.summary.work.acres) ? message.summary.work.acres : null;
        utilization = message.summary && message.summary.work && !isNaN(message.summary.work.utilization) ? message.summary.work.utilization : null;
    } else {
        let totalUtilization = 0, activeEGCount = 0;
        for (const egId in message) {
            if (!message[egId] || !Array.isArray(message[egId].data)) continue;
            const data = message[egId].data;

            let egUtilization = 0, activedDays = 0;
            for (const item of data) {
                const dayUtilization = item.summary && item.summary.work && !isNaN(item.summary.work.utilization) ? item.summary.work.utilization : 0;
                egUtilization += dayUtilization;
                activedDays += (dayUtilization > 0) ? 1 : 0;
                completedAcres += item.summary && item.summary.work && !isNaN(item.summary.work.acres) ? item.summary.work.acres : 0;
            }

            egUtilization = (activedDays > 0) ? (egUtilization / activedDays) : 0;
            totalUtilization += egUtilization;
            activeEGCount += (egUtilization > 0) ? 1 : 0;
        }
        utilization = (activeEGCount > 0) ? totalUtilization / activeEGCount : 0;
    }

    if (completedAcres === null) return;

    const egKPI = state.egKPIBoxList[params.egId];
    return {
        egKPIBoxList: {
            ...state.egKPIBoxList,
            [params.egId]: {
                ...egKPI,
                ...completedAcres > 0 && { completedAcres },
                ...utilization > 0 && { utilization },
                ...utilization > 0 && { egState: (utilization < 20 ? 3 : 1) }
            }
        }
    };
}

export const mergeMapEGSensors = (state, message, params) => {
    if (!state.egKPIBoxList[params.egId]) return;
    const batteryPercent = message['battery'] ? message['battery'].percent : null;
    const connected = message['qConnection'] ? message['qConnection'].connected : null;
    const fuelLevel = message['fuelLevel'] ? message['fuelLevel'].percent : null;
    const grainYield = message['grainYield'] ? message['grainYield'].bpa : null;
    const grainMoisture = message['grainMoisture'] ? message['grainMoisture'].percent : null;
    const ptoRPMSpeed = message['pto'] ? message['pto'].speed : null;
    const ptoRPMState = message['pto'] ? message['pto'].state : null;
    const speed = message["gps"] ? toMilesPerHour(message["gps"].speed) : null;
    const egKPI = state.egKPIBoxList[params.egId];

    return {
        egKPIBoxList: {
            ...state.egKPIBoxList, [params.egId]: {
                ...egKPI,
                batteryPercent, connected, grainYield, grainMoisture,
                ptoRPMSpeed, ptoRPMState, fuelLevel, speed
            }
        }
    };
}

export const mergeMapEGLoad = (state, message) => {
    if (!message) return;

    const egList = Array.isArray(message.equipment_groups) ? message.equipment_groups : [];
    const crewId = message.crew_uuid ? message.crew_uuid : null;
    if (!crewId) return;

    const getTripInfo = egId => {
        let loads = 0;
        let avgRoundTripLoadTime = { h: 0, m: 0 };
        const egItems = egList.filter(e => e.equipment_group_uuid === egId);

        if (egItems.length === 0 || !Array.isArray(egItems[0].trips)) return;
        const trips = egItems[0].trips.sort((a, b) => a.trip_id - b.trip_id);
        let prevStart = null, roundTripSeconds = 0, avgRoundTripSeconds = 0;

        for (const trip of trips) {
            const currentStart = moment.utc(trip.start_time);
            roundTripSeconds += prevStart ? currentStart.diff(prevStart, "seconds") : 0;
            prevStart = currentStart;
        }

        if (trips.length > 0) {
            loads = trips.length;
            avgRoundTripSeconds = (loads > 0) ? (roundTripSeconds / loads) : 0;
            avgRoundTripLoadTime = secondsToTime(avgRoundTripSeconds);
        }

        return {
            ...(loads > 0) ? { loads } : {},
            ...(avgRoundTripSeconds > 0) ? { avgRoundTripLoadTime } : {}
        };
    };

    let egKPIBoxList = { ...state.egKPIBoxList };
    for (const egId in state.egKPIBoxList) {
        const egItem = state.egKPIBoxList[egId];
        if (!egItem.hasOwnProperty("loads")) continue;
        if (egItem.crewId !== crewId) continue;

        const load = getTripInfo(egId);
        if (!load) continue;

        egKPIBoxList[egId] = { ...egItem, ...load };
    }

    return { egKPIBoxList };
}

export const mergeQPresentFromLive = (state, live) => {
    if (!live.message) return;
    const { egKPIBoxList } = state;
    const egItem = getEGItem(egKPIBoxList, live.deviceId);
    if (!egItem) return;

    const { speed } = getGPSFromLive(live.message);
    const { grainYield, grainMoisture, ptoRPMSpeed, fuelLevel, batteryPercent } = getCanFromLive(live.message);

    let egKPI = { ...egKPIBoxList[egItem.egId], connected: true };
    if (speed !== null) egKPI = { ...egKPI, speed };
    if (grainYield !== null) egKPI = { ...egKPI, grainYield };
    if (grainMoisture !== null) egKPI = { ...egKPI, grainMoisture };
    if (ptoRPMSpeed !== null) egKPI = { ...egKPI, ptoRPMSpeed };
    if (fuelLevel !== null) egKPI = { ...egKPI, fuelLevel };
    if (batteryPercent !== null) egKPI = { ...egKPI, batteryPercent };

    return { egKPIBoxList: { ...egKPIBoxList, [egItem.egId]: egKPI } };
}

// export const mergeQConnectionFromLive = (state, live) => {
// if (!live.message) return;
// const { egKPIBoxList } = state;
// const egItem = getEGItem(egKPIBoxList, live.deviceId);
// if (!egItem) return;

// const { message } = live;

// const qConnection = message.q_connection ? message.q_connection.data : message;
// let connected = null;

// if (qConnection.hasOwnProperty("connected"))
//     connected = qConnection.connected;
// else if (qConnection.hasOwnProperty("keep_alive_interval"))
//     connected = (parseInt(qConnection["keep_alive_interval"]) !== DEFAULT_QLOST_SPAN_SEC);
// else if (qConnection.hasOwnProperty("keep-alive-interval"))
//     connected = (parseInt(qConnection["keep-alive-interval"]) !== DEFAULT_QLOST_SPAN_SEC);

// if (connected !== null) {
//     const egKPI = { ...egKPIBoxList[egItem.egId], connected };
//     return { egKPIBoxList: { ...egKPIBoxList, [egItem.egId]: egKPI } };
// }

// return { egKPIBoxList };
// }

// const mergeQLostFromLive = (state, live) => {
// if (!live.message) return;
// const { egKPIBoxList } = state;
// const egItem = getEGItem(egKPIBoxList, live.deviceId);
// if (!egItem) return;

// const { message } = live;
// let connected = null;

// if (message.hasOwnProperty("connected"))
//     connected = message.connected;
// else if (message.hasOwnProperty("keep_alive_interval"))
//     connected = (parseInt(message["keep_alive_interval"]) !== DEFAULT_QLOST_SPAN_SEC);
// else if (message.hasOwnProperty("keep-alive-interval"))
//     connected = (parseInt(message["keep-alive-interval"]) !== DEFAULT_QLOST_SPAN_SEC);

// if (connected !== null) {
//     const egKPI = { ...egKPIBoxList[egItem.egId], connected };
//     return { egKPIBoxList: { ...egKPIBoxList, [egItem.egId]: egKPI } };
// }

// return { egKPIBoxList };
// }

const getEGItem = (boxList, deviceId) => {
    let egItem = null;

    for (const egId in boxList) {
        if (boxList[egId].deviceId === deviceId) {
            egItem = { egId, ...boxList[egId] };
            break;
        }
    }

    return egItem;
}

const getGPSFromLive = message => {
    const gps = { speed: null };
    const data = message.hasOwnProperty('gps') ? message.gps.data : message;
    gps.speed = toMilesPerHour(data.speed);
    return gps;
}

const getCanFromLive = message => {
    const can = {
        grainYield: null,
        grainMoisture: null,
        ptoRPMSpeed: null,
        fuelLevel: null,
        batteryPercent: null
    };

    const getSensor = sensor => {
        if (!message.hasOwnProperty(sensor)) return;
        return message[sensor].data;
    };

    const getSensorData = (sensor, keyName) => {
        const sensorData = getSensor(sensor);
        if (!sensorData) return null;
        return typeof sensorData === "object" && sensorData.hasOwnProperty(keyName)
            && Number(sensorData[keyName]) ? sensorData[keyName] : null;
    };

    can.grainYield = getSensorData("grain_yield", "bpa");
    can.ptoRPMSpeed = getSensorData("pto", "speed");
    can.ptoRPMState = getSensorData("pto", "state");
    can.fuelLevel = getSensorData("fuel_level", "percent");
    can.batteryPercent = getSensorData("battery_level", "percent");
    can.grainMoisture = getSensorData("grain_moisture", "percent_tenths");
    if (can.grainMoisture !== null) can.grainMoisture = (can.grainMoisture / 10);

    return can;
}